/**
 * Brand logo ball physics.
 *
 * Click the spinning logo and it drops, bounces and settles on the floor of the title section.
 * After that it can be picked up and flicked around, bouncing off the walls until it comes to rest.
 *
 * Hand-written rather than a physics library: one circle in one box needs Euler integration and a
 * wall check, and a library would be many times the size of the whole shell bundle.
 *
 * Nothing here runs until the logo is clicked, so the ordinary page cost is one listener.
 */

(() => {
  const GRAVITY = 2600; // px/s²
  const RESTITUTION = 0.62; // energy kept per bounce
  const WALL_RESTITUTION = 0.7;
  const AIR_DRAG = 0.999;
  const FLOOR_FRICTION = 0.82; // horizontal damping on floor contact
  const REST_SPEED = 26; // px/s below which a floor-resting ball is considered stopped
  const MAX_STEP = 1 / 30; // clamp long frames so a backgrounded tab cannot tunnel the ball
  const DRAG_THRESHOLD = 4;

  const field = document.querySelector('[data-ci-logo-field]');
  const logo = document.querySelector('[data-ci-logo]');
  if (!field || !logo) return;

  /*
   * Deliberately NOT gated on prefers-reduced-motion.
   *
   * That setting is about motion nobody asked for. The continuous spin is exactly that and stays
   * suppressed in CSS. The ball is the opposite: nothing moves until it is deliberately tapped or
   * dragged, and gating it meant the whole interaction was simply missing for anyone with Reduce
   * Motion switched on — which on iOS and iPadOS is a great many people.
   */

  let active = false;
  let raf = null;
  let last = 0;
  let shadow = null;

  let x = 0;
  let y = 0;
  let vx = 0;
  let vy = 0;
  let angle = 0;
  let size = 0;

  /* Drag state */
  let pointerId = null;
  let dragging = false;
  let moved = false;
  let grabX = 0;
  let grabY = 0;
  let lastPointer = { x: 0, y: 0, t: 0 };

  /* The wall the ball bounces off on the right — see bounds(). */
  const nameEl = document.querySelector('.ci-title__name');
  const TEXT_GAP = 16;

  /**
   * The field's own box ends at page centre, but the title text is right-aligned, so on a wide
   * window there is a large dead gap between the two. The right wall is therefore taken from where
   * the text actually starts rather than from the field's edge, and the ball is free to travel
   * across that gap.
   *
   * Only when the two are actually side by side: once the layout stacks on mobile the name sits
   * below the field, and its left edge would collapse the playable width to nothing.
   */
  const bounds = () => {
    const rect = field.getBoundingClientRect();
    let width = rect.width;

    if (nameEl) {
      const name = nameEl.getBoundingClientRect();
      const sideBySide = name.top < rect.bottom && name.bottom > rect.top;

      if (sideBySide) {
        width = Math.max(size, name.left - rect.left - TEXT_GAP);
      }
    }

    return { width, height: rect.height, rect };
  };

  function activate() {
    if (active) return;

    const fieldRect = field.getBoundingClientRect();
    const logoRect = logo.getBoundingClientRect();

    size = logoRect.width;
    x = logoRect.left - fieldRect.left;
    y = logoRect.top - fieldRect.top;
    vx = 0;
    vy = 0;

    /*
     * The CSS spin runs on the image, not the anchor. Read its current angle before freezing it, so
     * rotation continues from where it was instead of snapping back to zero.
     */
    const img = logo.querySelector('img');
    const spin = img ? getComputedStyle(img).transform : 'none';
    angle = spin && spin !== 'none' ? Math.atan2(...matrixParts(spin)) : 0;

    /*
     * The shadow already exists — it is drawn from the start so the resting logo reads as floating.
     * Physics just takes over positioning it, and the has-physics class stops its idle animation.
     */
    shadow = field.querySelector('.ci-title__logo-shadow');

    logo.classList.add('is-physics');
    field.classList.add('has-physics');
    active = true;

    draw();
    startVoice();
    start();
  }

  /* Pull the current rotation out of the animation's matrix so it does not jump on takeover. */
  function matrixParts(transform) {
    const values = transform.match(/matrix\(([^)]+)\)/);
    if (!values) return [0, 1];
    const [a, b] = values[1].split(',').map(Number);
    return [b, a];
  }

  /**
   * Starts the simulation only. Sound is started by the interaction handlers instead, so movement
   * the visitor did not cause — a resize nudging the ball back inside its box — stays silent rather
   * than starting a clip and immediately winding it down again.
   */
  function start() {
    if (raf) return;
    last = performance.now();

    /*
     * Low Power Mode can halve the frame rate. The simulation is integrated against real elapsed
     * time rather than assuming 60fps, so it runs at the same speed either way — just less smoothly.
     * MAX_STEP still guards against a single very long frame.
     */
    raf = requestAnimationFrame(step);
  }

  function stop() {
    if (raf) cancelAnimationFrame(raf);
    raf = null;
  }

  function step(now) {
    const dt = Math.min((now - last) / 1000, MAX_STEP);
    last = now;

    if (!dragging) {
      const { width, height } = bounds();
      const maxX = Math.max(0, width - size);
      const maxY = Math.max(0, height - size);

      vy += GRAVITY * dt;
      vx *= AIR_DRAG;

      x += vx * dt;
      y += vy * dt;

      /* Walls */
      if (x <= 0) {
        x = 0;
        vx = Math.abs(vx) * WALL_RESTITUTION;
      } else if (x >= maxX) {
        x = maxX;
        vx = -Math.abs(vx) * WALL_RESTITUTION;
      }

      /* Ceiling */
      if (y <= 0) {
        y = 0;
        vy = Math.abs(vy) * WALL_RESTITUTION;
      }

      /* Floor */
      if (y >= maxY) {
        y = maxY;
        vy = -Math.abs(vy) * RESTITUTION;
        vx *= FLOOR_FRICTION;

        /* Kill the micro-bounces that would otherwise buzz forever. */
        if (Math.abs(vy) < REST_SPEED) vy = 0;
      }

      /* Rolling: a circle on the floor turns in proportion to how far it travels. */
      const radius = size / 2;
      if (radius > 0) angle += (vx * dt) / radius;

      const settled =
        y >= maxY && Math.abs(vy) === 0 && Math.abs(vx) < REST_SPEED;

      if (settled) {
        vx = 0;
        draw();
        stop();

        /* The rate has already been sagging with the ball; this finishes the spin-down. */
        stopVoice();
        return;
      }

      updateVoice(Math.hypot(vx, vy));
    }

    draw();
    raf = requestAnimationFrame(step);
  }

  function draw() {
    logo.style.transform = `translate(${x}px, ${y}px) rotate(${angle}rad)`;
    drawShadow();
  }

  /**
   * An ellipse on the floor, tracking the ball horizontally and reading its height.
   *
   * It tightens and darkens as the ball nears the floor, and spreads and fades as it rises — the
   * cue that tells you how high something is when you cannot see depth directly. Both are driven by
   * the same 0..1 height, so they always agree.
   *
   * Only the transform and opacity change, so this costs no more per frame than moving the ball.
   */
  function drawShadow() {
    if (!shadow) return;

    const { height } = bounds();
    const floor = Math.max(1, height - size);

    /* 0 at the ceiling, 1 resting on the floor. */
    const grounded = Math.min(1, Math.max(0, y / floor));

    const spread = 1.45 - 0.45 * grounded;
    const alpha = 0.05 + 0.25 * grounded;

    shadow.style.width = `${size}px`;
    shadow.style.opacity = String(alpha);
    shadow.style.transform = `translateX(${x + size / 2}px) translateX(-50%) scale(${spread}, ${
      0.75 + 0.25 * grounded
    })`;
  }

  /* ---------------------------------------------------------------------------------------- */
  /* Audio                                                                                      */
  /* ---------------------------------------------------------------------------------------- */

  /**
   * A tape slow-down, done with Web Audio rather than <audio> elements.
   *
   * playbackRate on a buffer source is an AudioParam, so it can be ramped — and because it changes
   * playback speed rather than resampling, pitch falls with tempo. That coupling is what a tape or
   * turntable spin-down actually is. An <audio> element cannot be ramped smoothly and its rate
   * handling varies by browser, so it is the wrong tool here.
   *
   * The rate tracks the ball's speed the whole time it is moving, so the sound already sags as the
   * ball loses energy; the final ramp on rest just finishes the job. A lowpass closes alongside it,
   * because real tape loses its top end as it dies.
   */
  const VOLUME = 0.5;

  /*
   * Above this the clip plays at normal speed. Deliberately low: the point is that the track is
   * recognisable for as long as the ball is meaningfully moving, and only sags at the very end.
   */
  const FULL_SPEED = 220; // px/s

  /*
   * How the rate falls once below FULL_SPEED. Under 1 the curve stays high and drops away late,
   * which is what a tape stop sounds like — a linear map starts sagging immediately, which was the
   * original problem. Lower values hold normal speed longer.
   */
  const RATE_CURVE = 0.45;

  const MIN_RATE = 0.06;
  const TAPE_STOP = 0.9; // seconds of spin-down when the ball settles

  const soundUrls = (() => {
    const raw = field.getAttribute('data-ci-logo-sounds');

    if (!raw) {
      console.info('[ci-logo] no sound list on the field; ball will be silent');
      return [];
    }

    try {
      /* Each entry is a list of candidate URLs for one clip: assets first, then Files. */
      const parsed = JSON.parse(raw)
        .map((entry) => (Array.isArray(entry) ? entry.filter(Boolean) : [entry]).filter(Boolean))
        .filter((candidates) => candidates.length);

      if (!parsed.length) {
        console.info(
          '[ci-logo] sound list is empty. If the filenames are set, the files are probably not in ' +
            'Content -> Files yet: file_url returns nothing for a file that does not exist.'
        );
      }

      return parsed;
    } catch (error) {
      console.warn('[ci-logo] could not parse the sound list:', raw);
      return [];
    }
  })();

  let ctx = null;
  let buffers = null;
  let voice = null;

  /* Clips play in the order listed, advancing one per movement. */
  let nextSound = 0;

  function audioContext() {
    if (!ctx) {
      /* webkitAudioContext is still the only constructor on older iOS. */
      const Ctor = window.AudioContext || window.webkitAudioContext;
      if (!Ctor) return null;
      ctx = new Ctor();
    }

    /*
     * Browsers start contexts suspended until a gesture, and iOS additionally suspends them again
     * on its own — when the tab is backgrounded, on interruptions, and in Low Power Mode. Resuming
     * on every press rather than only on creation means a context that has been suspended since is
     * revived by the same tap that asks for sound.
     */
    if (ctx.state !== 'running') {
      const resumed = ctx.resume();
      if (resumed && resumed.catch) resumed.catch(() => {});
    }

    return ctx;
  }

  /**
   * Decoded in parallel but kept in the order they were listed. Pushing as each fetch resolved would
   * have ordered them by download speed, which does not matter for random picking but would scramble
   * a cycle differently on every visit.
   */
  async function loadBuffers() {
    if (buffers) return buffers;

    const decoded = await Promise.all(
      soundUrls.map(async (candidates) => {
        /* Try each location in turn; the first that answers and decodes wins. */
        for (const url of candidates) {
          try {
            const response = await fetch(url);
            if (!response.ok) continue;

            return await ctx.decodeAudioData(await response.arrayBuffer());
          } catch (error) {
            /*
             * 24-bit PCM WAV is the usual culprit here: Chrome and Firefox decode it, older Safari
             * does not. MP3 avoids the question entirely.
             */
            console.warn(`[ci-logo] could not decode ${url}:`, error.message);
          }
        }

        console.warn('[ci-logo] no source worked for:', candidates);
        return null;
      })
    );

    buffers = decoded.filter(Boolean);

    if (!buffers.length && soundUrls.length) {
      console.warn('[ci-logo] no clips loaded from:', soundUrls);
    }

    return buffers;
  }

  async function startVoice() {
    if (voice || !soundUrls.length || !audioContext()) return;
    await loadBuffers();
    if (!buffers.length || voice) return;

    /* The await above can outlast a suspension, so make sure the context is awake before playing. */
    if (ctx.state !== 'running') {
      const resumed = ctx.resume();
      if (resumed && resumed.catch) resumed.catch(() => {});
    }

    const source = ctx.createBufferSource();
    source.buffer = buffers[nextSound % buffers.length];
    nextSound = (nextSound + 1) % buffers.length;
    source.loop = true;

    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 16000;

    const gain = ctx.createGain();
    gain.gain.value = 0;
    gain.gain.setTargetAtTime(VOLUME, ctx.currentTime, 0.03);

    source.connect(filter).connect(gain).connect(ctx.destination);
    source.start();

    voice = { source, gain, filter };
  }

  /**
   * Full speed until the ball is genuinely slow, then a curve down to the floor. Mapping rate
   * straight from speed meant the track pitched down the moment the ball left its fastest, which
   * made it hard to hear what the clip actually was.
   */
  function speedToRate(speed) {
    if (speed >= FULL_SPEED) return 1;

    const t = Math.max(speed, 0) / FULL_SPEED;
    return MIN_RATE + (1 - MIN_RATE) * Math.pow(t, RATE_CURVE);
  }

  function updateVoice(speed) {
    if (!voice || !ctx) return;

    const rate = speedToRate(speed);
    const now = ctx.currentTime;

    /* setTargetAtTime rather than direct assignment, so per-frame changes glide instead of zipper. */
    voice.source.playbackRate.setTargetAtTime(rate, now, 0.05);
    voice.filter.frequency.setTargetAtTime(400 + rate * 15000, now, 0.05);
  }

  function stopVoice(spinDown = TAPE_STOP) {
    if (!voice || !ctx) return;

    const { source, gain, filter } = voice;
    voice = null;

    const now = ctx.currentTime;

    if (spinDown <= 0) {
      gain.gain.cancelScheduledValues(now);
      gain.gain.setTargetAtTime(0, now, 0.01);
      source.stop(now + 0.1);
      return;
    }

    /* Pin the current values first, or the ramp starts from whatever was last scheduled. */
    source.playbackRate.cancelScheduledValues(now);
    source.playbackRate.setValueAtTime(source.playbackRate.value, now);

    /* Never exactly zero — some browsers treat a zero rate as invalid rather than as silence. */
    source.playbackRate.linearRampToValueAtTime(0.02, now + spinDown);

    filter.frequency.cancelScheduledValues(now);
    filter.frequency.setValueAtTime(filter.frequency.value, now);
    filter.frequency.exponentialRampToValueAtTime(180, now + spinDown);

    /* Fade out over the tail so the spin-down is heard but does not end on a click. */
    gain.gain.setTargetAtTime(0, now + spinDown * 0.55, 0.18);
    source.stop(now + spinDown + 0.5);
  }

  /* ---------------------------------------------------------------------------------------- */
  /* Interaction                                                                                */
  /* ---------------------------------------------------------------------------------------- */

  logo.addEventListener('pointerdown', (event) => {
    if (event.button !== 0 && event.pointerType === 'mouse') return;

    /*
     * Create and resume the context inside the gesture, and start fetching. Doing this only at play
     * time meant the first sound waited on a network round trip, and Safari can refuse a context
     * resumed after an await because the gesture is considered over.
     */
    if (audioContext()) loadBuffers();

    if (!active) {
      /* First press arms physics; the drop happens on release if it was a click. */
      pointerId = event.pointerId;
      moved = false;
      lastPointer = { x: event.clientX, y: event.clientY, t: event.timeStamp };
      grabX = event.clientX;
      grabY = event.clientY;
      return;
    }

    pointerId = event.pointerId;
    dragging = false;
    moved = false;

    const fieldRect = field.getBoundingClientRect();
    grabX = event.clientX - fieldRect.left - x;
    grabY = event.clientY - fieldRect.top - y;
    lastPointer = { x: event.clientX, y: event.clientY, t: event.timeStamp };
  });

  window.addEventListener('pointermove', (event) => {
    if (event.pointerId !== pointerId) return;

    const dx = event.clientX - lastPointer.x;
    const dy = event.clientY - lastPointer.y;

    if (!moved && Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) return;

    /* A drag before activation still activates, so the logo can be thrown straight away. */
    if (!active) {
      activate();
      const fieldRect = field.getBoundingClientRect();
      grabX = event.clientX - fieldRect.left - x;
      grabY = event.clientY - fieldRect.top - y;
    }

    moved = true;
    dragging = true;
    event.preventDefault();

    const { width, height } = bounds();
    const fieldRect = field.getBoundingClientRect();

    x = Math.min(Math.max(event.clientX - fieldRect.left - grabX, 0), Math.max(0, width - size));
    y = Math.min(Math.max(event.clientY - fieldRect.top - grabY, 0), Math.max(0, height - size));

    /* Velocity comes from actual pointer movement, so a flick carries its real speed. */
    const dt = Math.max((event.timeStamp - lastPointer.t) / 1000, 0.001);
    vx = (event.clientX - lastPointer.x) / dt;
    vy = (event.clientY - lastPointer.y) / dt;

    lastPointer = { x: event.clientX, y: event.clientY, t: event.timeStamp };

    /* A ball being dragged is moving, so it plays too, following the hand's speed. */
    startVoice();
    updateVoice(Math.hypot(vx, vy));

    draw();
  }, { passive: false });

  const release = () => {
    if (pointerId === null) return;

    if (!active && !moved) {
      /* A clean click: drop it. */
      activate();
    }

    if (dragging) {
      /* Cap a flick so an enthusiastic throw stays on screen. */
      const cap = 4000;
      vx = Math.max(Math.min(vx, cap), -cap);
      vy = Math.max(Math.min(vy, cap), -cap);
      startVoice();
      start();
    }

    /* Winding down applies to letting go of a held ball, not to the click that first drops it. */
    if (!dragging && moved) stopVoice();

    dragging = false;
    pointerId = null;
  };

  window.addEventListener('pointerup', release);

  /*
   * A cancelled pointer — the browser taking the gesture, or a touch leaving the screen edge —
   * still has to end the drag, or the ball would hang wherever it was last dragged to.
   */
  window.addEventListener('pointercancel', release);

  /*
   * Firefox ignores -webkit-user-drag, so the native drag is cancelled here too. Without both, the
   * browser paints a ghost of the image following the cursor over the page.
   */
  logo.addEventListener('dragstart', (event) => event.preventDefault());

  /* Once physics owns the logo the link would be a trap, so navigation is suppressed. */
  logo.addEventListener('click', (event) => {
    if (active || moved) event.preventDefault();
  });

  /* The field changes size on resize; keep the ball inside it. */
  window.addEventListener('resize', () => {
    if (!active) return;

    const { width, height } = bounds();
    x = Math.min(x, Math.max(0, width - size));
    y = Math.min(y, Math.max(0, height - size));
    draw();
    start();
  });
})();
